import { useEffect, useMemo, useState } from 'react'
import {
  Monitor, Keyboard, Cpu, BatteryCharging, Plug, Move, Box, HelpCircle,
  Phone, MessageCircle, MapPin, Clock, Mail, Star, ShieldCheck, Wrench,
  Award, ThumbsUp, ChevronDown, ArrowRight, Sparkles, ClipboardList, Gamepad2 } from 'lucide-react'

const API_BASE = ''

// IDs de tracking. Si META_PIXEL_ID está vacío, el snippet no se inyecta.
const TRACKING = {
  GA4_ID: 'G-QLJPTD6PZS',
  META_PIXEL_ID: '1342806854421616',
}

const STORE = {
  name: 'JVS Informática',
  address: 'Urb. La Reguera Nº 231 bajo Izq.',
  postcode: '39591',
  city: 'Casar de Periedo',
  region: 'Cantabria',
  phone: '+34 942 71 97 35',
  phoneTel: '+34942719735',
  whatsapp: '+34 644 46 49 60',
  whatsappRaw: '34644464960',
  email: 'info@jvs-informatica.com',
  hours: [
    { d: 'Lunes a Jueves', h: '9:00 – 16:30 (jornada continuada)' },
    { d: 'Viernes', h: '9:00 – 15:30' },
    { d: 'Sábado y Domingo', h: 'Cerrado' },
  ],
  mapsAddress: 'Urb. La Reguera 231, 39591 Casar de Periedo, Cantabria',
}

const WHATSAPP_TEXT = encodeURIComponent(
  'Hola, me gustaría información sobre la reparación de mi portátil.'
)

const REPAIR_TYPES_META: Record<string, { icon: any; pitch: string }> = {
  screen: { icon: Monitor, pitch: 'Cambio de pantalla rota, con líneas o sin imagen.' },
  keyboard: { icon: Keyboard, pitch: 'Teclas que no responden, líquidos, cambio completo.' },
  motherboard: { icon: Cpu, pitch: 'Micro-soldadura, no enciende, daños por líquidos.' },
  battery: { icon: BatteryCharging, pitch: 'Batería hinchada, no carga, autonomía baja.' },
  power: { icon: Plug, pitch: 'Conector de carga suelto o quemado.' },
  hinge: { icon: Move, pitch: 'Bisagras rotas o tapa que no sostiene.' },
  casing: { icon: Box, pitch: 'Carcasa fisurada, marcos rotos.' },
  general: { icon: HelpCircle, pitch: '¿No sabes qué falla? Diagnóstico gratuito y presupuesto por escrito.' },
  console: { icon: Gamepad2, pitch: 'PS4/PS5, Xbox, Switch, Steam Deck, ROG Ally: limpieza, pantalla, mandos.' },
}

type RepairType = { code: string; name: string; icon: string }
type Estimate = {
  estimate_min: number
  estimate_max: number
  days_min: number
  days_max: number
}
type Review = {
  author: string
  rating: number
  text: string
  time: string
  profile_photo?: string
}
type ReviewsData = {
  rating: number
  total_reviews: number
  reviews: Review[]
  google_review_url: string
  place_url?: string
}

function track(event: string, params: Record<string, any> = {}) {
  try {
    ;(window as any).gtag?.('event', event, params)
    ;(window as any).fbq?.('trackCustom', event, params)
  } catch {}
}

function injectTracking() {
  if (typeof window === 'undefined') return
  const w = window as any

  // ---- GA4 ----
  if (TRACKING.GA4_ID && !w.__ga4Loaded) {
    w.__ga4Loaded = true
    const s = document.createElement('script')
    s.async = true
    s.src = `https://www.googletagmanager.com/gtag/js?id=${TRACKING.GA4_ID}`
    document.head.appendChild(s)
    w.dataLayer = w.dataLayer || []
    w.gtag = function () { w.dataLayer.push(arguments) }
    w.gtag('js', new Date())
    w.gtag('config', TRACKING.GA4_ID, {
      page_path: window.location.pathname,
      content_group: 'landing_reparaciones',
      send_page_view: true,
    })
  }

  // ---- Meta Pixel ----
  if (TRACKING.META_PIXEL_ID && !w.__fbqLoaded) {
    w.__fbqLoaded = true
    ;(function (f: any, b: any, e: any, v: any) {
      if (f.fbq) return
      const n: any = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
      }
      if (!f._fbq) f._fbq = n
      n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = []
      const t = b.createElement(e); t.async = !0; t.src = v
      const s = b.getElementsByTagName(e)[0]
      s.parentNode.insertBefore(t, s)
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js')
    w.fbq('init', TRACKING.META_PIXEL_ID)
    w.fbq('track', 'PageView')
  }
}

function StarRow({ rating, size = 14 }: { rating: number; size?: number }) {
  return (
    <div className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star
          key={i}
          size={size}
          className={
            i <= Math.round(rating)
              ? 'fill-amber-400 text-amber-400'
              : 'text-slate-300'
          }
        />
      ))}
    </div>
  )
}

function GoogleLogo({ size = 18 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  )
}

const FAQ: { q: string; a: string }[] = [
  {
    q: '¿Cuánto cuesta el diagnóstico?',
    a: 'El diagnóstico es GRATUITO, en tienda y por mensajería. Si traes el equipo a la tienda y no aceptas el presupuesto o no tiene arreglo, no pagas nada. Si lo recogemos por mensajería, los portes de recogida y devolución (29,90 € IVA incl. para particulares, 11,90 € para profesionales) van como línea aparte del presupuesto y son lo único que se cobra si no hay reparación.',
  },
  {
    q: '¿Cuánto se tarda en una reparación?',
    a: 'Las reparaciones más habituales (pantalla, batería, teclado) las terminamos en 2-7 días laborables. Reparaciones de placa base con micro-soldadura pueden requerir hasta 15 días según disponibilidad de componentes.',
  },
  {
    q: '¿Reparáis cualquier marca?',
    a: 'Sí: Acer, Apple, ASUS, Dell, HP, Huawei, Lenovo, LG, Microsoft, MSI, Razer, Samsung, Toshiba, Xiaomi y otras. Llevamos más de 10 años trabajando con todas las marcas del mercado.',
  },
  {
    q: '¿Dais garantía en la reparación?',
    a: 'Sí: 3 meses de garantía sobre la pieza sustituida y la mano de obra, documentada en la factura.',
  },
  {
    q: '¿Puedo enviaros el portátil por mensajería?',
    a: 'Sí. Si no te queda cerca, organizamos la recogida y la devolución por mensajería desde nuestro portal de reparaciones. Los portes (29,90 € IVA incl. para particulares) se muestran como línea aparte en el presupuesto y son lo único que pagas si rechazas el presupuesto o el equipo no tiene arreglo.',
  },
  {
    q: '¿Hacéis recuperación de datos de discos dañados?',
    a: 'Sí, recuperamos datos de discos duros y SSDs dañados, con fallos lógicos o eléctricos. Es un servicio sensible: te damos presupuesto cerrado antes de actuar.',
  },
]

export default function RepairsLandingPage() {
  const [reviews, setReviews] = useState<ReviewsData | null>(null)
  const [types, setTypes] = useState<RepairType[]>([])
  const [estimates, setEstimates] = useState<Record<string, Estimate>>({})
  const [openFaq, setOpenFaq] = useState<number | null>(0)

  useEffect(() => {
    document.title = 'Reparación de portátiles en Cantabria · JVS Informática'
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement('meta'); m.name = 'description'; document.head.appendChild(m); return m
    })()
    meta.setAttribute('content',
      'Servicio técnico especializado en reparación de portátiles en Casar de Periedo (Cantabria). Diagnóstico gratuito, presupuesto sin compromiso. Más de 10 años reparando.'
    )
    injectTracking()
    track('landing_view', { page: 'reparacion_portatiles_cantabria' })
  }, [])

  useEffect(() => {
    fetch(`${API_BASE}/api/google-reviews/widget`)
      .then(r => r.json())
      .then(setReviews)
      .catch(() => {})
  }, [])

  useEffect(() => {
    fetch(`${API_BASE}/api/repairs/types`)
      .then(r => r.json())
      .then((data) => {
        setTypes(data.types || [])
        // Cargar estimaciones en paralelo
        const codes: string[] = (data.types || []).map((t: RepairType) => t.code)
        return Promise.all(codes.map(code =>
          fetch(`${API_BASE}/api/repairs/estimate?repair_type=${code}`, { method: 'POST' })
            .then(r => r.json())
            .then(e => ({ code, e }))
            .catch(() => ({ code, e: null }))
        ))
      })
      .then((results) => {
        if (!results) return
        const acc: Record<string, Estimate> = {}
        for (const r of results) {
          if (r.e && typeof r.e.estimate_min === 'number') acc[r.code] = r.e
        }
        setEstimates(acc)
      })
      .catch(() => {})
  }, [])

  const ratingDisplay = reviews?.rating?.toFixed(1) || '4.9'
  const totalReviews = reviews?.total_reviews ?? 0

  const mapsEmbedSrc = useMemo(() =>
    `https://www.google.com/maps?q=${encodeURIComponent(STORE.mapsAddress)}&output=embed`,
    [])
  const mapsDirSrc = useMemo(() =>
    `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(STORE.mapsAddress)}`,
    [])

  const callHref = `tel:${STORE.phoneTel}`
  const waHref = `https://wa.me/${STORE.whatsappRaw}?text=${WHATSAPP_TEXT}`
  const formHref = (src: string) => `/repairs?source=landing_${src}`

  const onCall = () => track('contact_phone', { source: 'landing_repairs' })
  const onWa = () => track('contact_whatsapp', { source: 'landing_repairs' })
  const onForm = (where: string) => track('open_repair_form', { source: 'landing_repairs', where })
  const onLead = (where: string) => track('generate_lead', { source: 'landing_repairs', where })

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      {/* ============ TOP BAR ============ */}
      <div className="bg-slate-900 text-slate-300 text-xs">
        <div className="max-w-6xl mx-auto px-4 py-2 flex items-center justify-center sm:justify-end gap-1.5">
          <span className="hidden sm:inline">¿Buscas componentes para enviar a toda España?</span>
          <span className="sm:hidden">Compra componentes online</span>
          <a
            href="https://www.jvs-informatica.com?utm_source=landing&utm_medium=topbar&utm_campaign=cross_to_store"
            target="_blank" rel="noopener"
            onClick={() => track('cross_to_store', { source: 'landing_repairs', where: 'topbar' })}
            className="font-semibold text-amber-300 hover:text-amber-200 underline-offset-2 hover:underline"
          >
            Visita nuestra tienda online →
          </a>
        </div>
      </div>

      {/* ============ HEADER ============ */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center shadow-md">
              <Wrench className="text-white" size={20} />
            </div>
            <div>
              <div className="font-bold text-slate-900 leading-tight">JVS Informática</div>
              <div className="text-[11px] text-slate-500 leading-tight">Reparación de portátiles · Cantabria</div>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-2">
            <a href={callHref} onClick={onCall} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 transition text-sm font-medium">
              <Phone size={16} /> {STORE.phone}
            </a>
            <a href={waHref} onClick={onWa} target="_blank" rel="noopener" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white transition text-sm font-medium shadow-sm">
              <MessageCircle size={16} /> WhatsApp
            </a>
          </div>
          <a href={waHref} onClick={onWa} target="_blank" rel="noopener" className="md:hidden inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 text-white text-sm font-medium">
            <MessageCircle size={14} /> Chat
          </a>
        </div>
      </header>

      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-950 via-indigo-900 to-violet-900" />
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.15) 0, transparent 40%), radial-gradient(circle at 80% 80%, rgba(168,85,247,0.25) 0, transparent 40%)'
        }} />
        <div className="relative max-w-6xl mx-auto px-4 py-16 md:py-24">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 text-white text-xs font-medium px-3 py-1.5 rounded-full mb-5">
                <Sparkles size={14} className="text-amber-300" />
                Nueva tienda en Casar de Periedo · Cabezón de la Sal
              </div>
              <h1 className="text-4xl md:text-5xl font-extrabold text-white leading-tight mb-4">
                Reparación de portátiles en <span className="text-amber-300">Cantabria</span>
              </h1>
              <p className="text-lg text-indigo-100 mb-6 leading-relaxed">
                Diagnóstico <strong className="text-white">100% gratuito</strong>, presupuesto sin compromiso y reparación profesional. Llevamos <strong className="text-white">más de 10 años</strong> reparando portátiles de todas las marcas.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={waHref}
                  onClick={() => { onWa(); onLead('hero_whatsapp') }}
                  target="_blank" rel="noopener"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-green-500 hover:bg-green-600 text-white font-semibold shadow-lg transition transform hover:-translate-y-0.5"
                >
                  <MessageCircle size={20} /> Pídenos presupuesto gratis
                </a>
                <a
                  href={callHref}
                  onClick={() => { onCall(); onLead('hero_phone') }}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-white/10 hover:bg-white/15 backdrop-blur border border-white/20 text-white font-semibold transition"
                >
                  <Phone size={18} /> {STORE.phone}
                </a>
              </div>
              {reviews && (
                <a
                  href={reviews.place_url || reviews.google_review_url}
                  target="_blank" rel="noopener"
                  className="inline-flex items-center gap-3 mt-6 text-white/90 hover:text-white transition group"
                >
                  <GoogleLogo size={20} />
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-lg">{ratingDisplay}</span>
                    <StarRow rating={reviews.rating || 4.9} size={16} />
                    <span className="text-sm text-indigo-200 group-hover:underline">
                      {totalReviews ? `${totalReviews} opiniones` : 'opiniones en Google'}
                    </span>
                  </div>
                </a>
              )}
            </div>

            {/* Tarjeta lateral de contacto */}
            <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-7">
              <h3 className="text-lg font-bold text-slate-900 mb-1">Tu portátil, en buenas manos</h3>
              <p className="text-sm text-slate-600 mb-5">Cuéntanos qué le pasa y te damos presupuesto sin compromiso.</p>
              <div className="space-y-3">
                <a href={waHref} onClick={() => { onWa(); onLead('card_whatsapp') }} target="_blank" rel="noopener" className="flex items-center justify-between p-4 rounded-xl bg-green-50 hover:bg-green-100 border border-green-200 transition group">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-600 flex items-center justify-center text-white"><MessageCircle size={18} /></div>
                    <div>
                      <div className="font-semibold text-slate-900">WhatsApp</div>
                      <div className="text-sm text-slate-600">{STORE.whatsapp}</div>
                    </div>
                  </div>
                  <ArrowRight className="text-green-700 group-hover:translate-x-1 transition" size={18} />
                </a>
                <a href={callHref} onClick={() => { onCall(); onLead('card_phone') }} className="flex items-center justify-between p-4 rounded-xl bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 transition group">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-600 flex items-center justify-center text-white"><Phone size={18} /></div>
                    <div>
                      <div className="font-semibold text-slate-900">Llamada directa</div>
                      <div className="text-sm text-slate-600">{STORE.phone}</div>
                    </div>
                  </div>
                  <ArrowRight className="text-indigo-700 group-hover:translate-x-1 transition" size={18} />
                </a>
                <a href={`mailto:${STORE.email}`} onClick={() => onLead('card_email')} className="flex items-center justify-between p-4 rounded-xl bg-slate-50 hover:bg-slate-100 border border-slate-200 transition group">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center text-white"><Mail size={18} /></div>
                    <div>
                      <div className="font-semibold text-slate-900">Email</div>
                      <div className="text-sm text-slate-600">{STORE.email}</div>
                    </div>
                  </div>
                  <ArrowRight className="text-slate-700 group-hover:translate-x-1 transition" size={18} />
                </a>
              </div>
              <a href={formHref('card_form')} onClick={() => { onForm('card'); onLead('card_form') }} className="flex items-center justify-center gap-1.5 mt-4 text-sm font-medium text-indigo-700 hover:underline">
                <ClipboardList size={14} />
                ¿No puedes venir? Recogida por mensajería →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ============ TRUST BAR ============ */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="flex items-center gap-3">
            <ShieldCheck className="text-emerald-600 shrink-0" size={28} />
            <div>
              <div className="font-semibold text-slate-900 text-sm">Diagnóstico gratuito</div>
              <div className="text-xs text-slate-500">En tienda, sin ningún coste</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Award className="text-amber-500 shrink-0" size={28} />
            <div>
              <div className="font-semibold text-slate-900 text-sm">+10 años de experiencia</div>
              <div className="text-xs text-slate-500">Todas las marcas</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ThumbsUp className="text-indigo-600 shrink-0" size={28} />
            <div>
              <div className="font-semibold text-slate-900 text-sm">{ratingDisplay} / 5 en Google</div>
              <div className="text-xs text-slate-500">{totalReviews ? `${totalReviews} opiniones` : 'Cientos de opiniones'}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Wrench className="text-violet-600 shrink-0" size={28} />
            <div>
              <div className="font-semibold text-slate-900 text-sm">3 meses de garantía</div>
              <div className="text-xs text-slate-500">Pieza y mano de obra</div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SERVICIOS ============ */}
      <section id="servicios" className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <div className="inline-block px-3 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold mb-3">SERVICIOS</div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">Reparamos cualquier avería</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Cambio de piezas, micro-soldadura en placa base, recuperación de datos, instalación de SSD, eliminación de virus.
              Trabajamos con todas las marcas: Acer, Apple, ASUS, Dell, HP, Lenovo, MSI, Samsung, Toshiba y más.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {types.map(t => {
              const meta = REPAIR_TYPES_META[t.code] || REPAIR_TYPES_META.general
              const Icon = meta.icon
              const est = estimates[t.code]
              return (
                <div key={t.code} className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-lg hover:border-indigo-300 transition group">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white mb-4 group-hover:scale-110 transition">
                    <Icon size={22} />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-1">{t.name}</h3>
                  <p className="text-sm text-slate-600 mb-3 leading-relaxed">{meta.pitch}</p>
                  {est ? (
                    <div className="text-xs text-slate-500">
                      <div>Desde <span className="font-semibold text-slate-900">{Math.round(est.estimate_min)} €</span></div>
                      <div>Plazo {est.days_min}-{est.days_max} días</div>
                    </div>
                  ) : (
                    <div className="text-xs text-slate-400">Presupuesto gratuito</div>
                  )}
                </div>
              )
            })}
          </div>

          <div className="mt-8 p-6 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl flex flex-col md:flex-row md:items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-amber-500 flex items-center justify-center text-white shrink-0">
              <Sparkles size={26} />
            </div>
            <div className="flex-1">
              <div className="font-bold text-slate-900">¿No estás seguro de qué le pasa a tu portátil?</div>
              <p className="text-sm text-slate-700">Tráelo a la tienda. Lo diagnosticamos GRATIS y te decimos exactamente qué tiene y cuánto cuesta repararlo. Si no quieres seguir, te lo llevas sin coste. Si prefieres mensajería, los portes van aparte.</p>
            </div>
            <a href={waHref} onClick={() => { onWa(); onLead('cta_diagnostic') }} target="_blank" rel="noopener" className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold transition whitespace-nowrap">
              <MessageCircle size={18} /> Pídenos diagnóstico
            </a>
          </div>
        </div>
      </section>

      {/* ============ RESEÑAS GOOGLE ============ */}
      <section id="resenas" className="py-16 md:py-20 bg-white border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold mb-3">
              <GoogleLogo size={14} /> OPINIONES VERIFICADAS
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">Lo que dicen nuestros clientes</h2>
            <div className="inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-slate-50 border border-slate-200">
              <span className="text-3xl font-bold text-slate-900">{ratingDisplay}</span>
              <div>
                <StarRow rating={reviews?.rating || 4.9} size={18} />
                <div className="text-xs text-slate-500 mt-0.5">
                  {totalReviews ? `${totalReviews} opiniones en Google` : 'Cientos de opiniones en Google'}
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {(reviews?.reviews || []).slice(0, 6).map((r, i) => (
              <div key={i} className="bg-slate-50 border border-slate-200 rounded-2xl p-5 flex flex-col">
                <div className="flex items-start gap-3 mb-3">
                  {r.profile_photo ? (
                    <img src={r.profile_photo} alt={r.author} className="w-10 h-10 rounded-full" referrerPolicy="no-referrer" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center font-semibold text-sm">
                      {r.author?.[0] || '?'}
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="font-semibold text-slate-900 text-sm">{r.author}</div>
                    <div className="flex items-center gap-2">
                      <StarRow rating={r.rating} />
                      <span className="text-xs text-slate-500">{r.time}</span>
                    </div>
                  </div>
                  <GoogleLogo size={16} />
                </div>
                <p className="text-sm text-slate-700 leading-relaxed flex-1">{r.text}</p>
              </div>
            ))}
            {(!reviews || reviews.reviews.length === 0) && (
              <div className="md:col-span-2 lg:col-span-3 text-center text-slate-500 py-8">
                Cargando opiniones…
              </div>
            )}
          </div>

          {reviews?.google_review_url && (
            <div className="text-center mt-8">
              <a
                href={reviews.google_review_url}
                target="_blank" rel="noopener"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-slate-300 hover:bg-slate-50 transition font-medium text-slate-700"
              >
                <GoogleLogo size={16} /> Ver todas las opiniones en Google
              </a>
            </div>
          )}
        </div>
      </section>

      {/* ============ CÓMO LLEGAR ============ */}
      <section id="como-llegar" className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <div className="inline-block px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold mb-3">VISÍTANOS</div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">Estamos en Casar de Periedo</h2>
            <p className="text-slate-600">A 10 min de Torrelavega · 35 min de Santander · 15 min de Comillas</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 items-stretch">
            <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm min-h-[320px] bg-slate-100">
              <iframe
                title="Mapa JVS Informática"
                src={mapsEmbedSrc}
                width="100%" height="100%"
                style={{ border: 0, minHeight: 360 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-7">
              <div className="flex items-start gap-3 mb-5">
                <MapPin className="text-indigo-600 shrink-0 mt-1" size={22} />
                <div>
                  <div className="font-bold text-slate-900 mb-0.5">{STORE.name}</div>
                  <div className="text-slate-700">{STORE.address}</div>
                  <div className="text-slate-700">{STORE.postcode} {STORE.city}, {STORE.region}</div>
                </div>
              </div>
              <div className="flex items-start gap-3 mb-5">
                <Clock className="text-indigo-600 shrink-0 mt-1" size={22} />
                <div>
                  <div className="font-bold text-slate-900 mb-2">Horario</div>
                  {STORE.hours.map(h => (
                    <div key={h.d} className="text-sm text-slate-700 flex justify-between gap-6 py-0.5">
                      <span className="text-slate-500">{h.d}</span>
                      <span className="font-medium">{h.h}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <a href={mapsDirSrc} target="_blank" rel="noopener" onClick={() => onLead('directions')} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold transition">
                  <MapPin size={18} /> Cómo llegar
                </a>
                <a href={callHref} onClick={onCall} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold transition">
                  <Phone size={18} /> Llamar
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="py-16 md:py-20 bg-white border-t border-slate-200">
        <div className="max-w-3xl mx-auto px-4">
          <div className="text-center mb-10">
            <div className="inline-block px-3 py-1 rounded-full bg-violet-100 text-violet-700 text-xs font-semibold mb-3">PREGUNTAS FRECUENTES</div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">¿Tienes dudas?</h2>
          </div>
          <div className="space-y-3">
            {FAQ.map((f, i) => {
              const open = openFaq === i
              return (
                <div key={i} className="border border-slate-200 rounded-xl overflow-hidden bg-slate-50">
                  <button
                    onClick={() => setOpenFaq(open ? null : i)}
                    className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-slate-100 transition"
                  >
                    <span className="font-semibold text-slate-900">{f.q}</span>
                    <ChevronDown className={`text-slate-500 transition-transform shrink-0 ${open ? 'rotate-180' : ''}`} size={18} />
                  </button>
                  {open && (
                    <div className="px-5 pb-4 text-sm text-slate-700 leading-relaxed">{f.a}</div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ============ CTA FINAL ============ */}
      <section className="relative py-16 md:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900 via-violet-900 to-fuchsia-900" />
        <div className="relative max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Tu portátil tiene arreglo. Te lo decimos GRATIS.
          </h2>
          <p className="text-indigo-100 text-lg mb-8">
            Diagnóstico gratuito y presupuesto cerrado por escrito antes de tocar nada. 3 meses de garantía.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href={waHref} onClick={() => { onWa(); onLead('final_whatsapp') }} target="_blank" rel="noopener" className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-green-500 hover:bg-green-600 text-white font-bold shadow-lg transition transform hover:-translate-y-0.5">
              <MessageCircle size={20} /> Escríbenos por WhatsApp
            </a>
            <a href={callHref} onClick={() => { onCall(); onLead('final_phone') }} className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-white/10 hover:bg-white/15 backdrop-blur border border-white/20 text-white font-bold transition">
              <Phone size={20} /> {STORE.phone}
            </a>
          </div>
          <p className="text-xs text-indigo-200 mt-6">
            Diagnóstico y presupuesto gratuitos. En tienda no pagas nada si no reparas; por mensajería solo los portes. Garantía de 3 meses.
          </p>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="bg-slate-950 text-slate-300 py-10">
        <div className="max-w-6xl mx-auto px-4 grid md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center"><Wrench size={18} className="text-white" /></div>
              <div className="font-bold text-white">JVS Informática</div>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed">
              Servicio técnico especializado en reparación de portátiles. Casar de Periedo (Cantabria).
            </p>
          </div>
          <div>
            <div className="text-xs uppercase text-slate-500 font-semibold mb-3">Contacto</div>
            <ul className="space-y-1.5 text-sm">
              <li><a href={callHref} className="hover:text-white">{STORE.phone}</a></li>
              <li><a href={waHref} target="_blank" rel="noopener" className="hover:text-white">WhatsApp: {STORE.whatsapp}</a></li>
              <li><a href={`mailto:${STORE.email}`} className="hover:text-white">{STORE.email}</a></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase text-slate-500 font-semibold mb-3">Dirección</div>
            <p className="text-sm text-slate-400 leading-relaxed">
              {STORE.address}<br />
              {STORE.postcode} {STORE.city}<br />
              {STORE.region}
            </p>
            <a href="https://www.jvs-informatica.com" className="inline-block mt-3 text-sm text-indigo-400 hover:text-indigo-300">jvs-informatica.com →</a>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 mt-8 pt-6 border-t border-slate-800 text-xs text-slate-500">
          © {new Date().getFullYear()} JVS Informática. Todos los derechos reservados.
        </div>
      </footer>

      {/* ============ Sticky WhatsApp en móvil ============ */}
      <a
        href={waHref}
        onClick={() => { onWa(); onLead('sticky_whatsapp') }}
        target="_blank" rel="noopener"
        className="md:hidden fixed bottom-4 right-4 z-40 inline-flex items-center gap-2 px-4 py-3 rounded-full bg-green-500 hover:bg-green-600 text-white font-semibold shadow-xl"
        aria-label="WhatsApp"
      >
        <MessageCircle size={22} />
      </a>
    </div>
  )
}
